import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import chroma from 'chroma-js';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import GrainOutlinedIcon from '@mui/icons-material/GrainOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import ThunderstormOutlinedIcon from '@mui/icons-material/ThunderstormOutlined';
import BlurOnOutlinedIcon from '@mui/icons-material/BlurOnOutlined';
import NodeAura from '../components/dynamic/NodeAura';
import NodeBOS from '../components/dynamic/NodeBOS';
import NodePulse from '../components/dynamic/NodePulse';
import NodeMaestro from '../components/dynamic/NodeMaestro';
import Text from '../components/Text'
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

const gentleFloat = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(10px, -8px, 0) scale(1.02); }
`;

const slideReveal = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SLIDES = [
  {
    type: 'tip',
    label: 'BOS',
    text: 'Centralisez et structurez vos opérations dans un environnement de gestion clair, connecté et évolutif.',
  },
  {
    type: 'tip',
    label: 'Astuce',
    text: 'Centralisez vos suivis pour réduire les frictions entre vos équipes et garder une meilleure continuité opérationnelle.',
  },
  {
    type: 'tip',
    label:'Aura',
    text: 'Créez des portails clients intelligents pour fluidifier les échanges, les demandes et l’accès à l’information.',
  },
  {
    type: 'tip',
    label: 'Astuce',
    text: 'Automatisez vos processus récurrents pour gagner en efficacité et libérer du temps sur les tâches à plus forte valeur.',
  },
  {
    type: 'tip',
    label: 'Pulse',
    text: 'Suivez vos indicateurs, visualisez vos données et obtenez une lecture claire de vos opérations en temps réel.',
  },
  {
    type: 'tip',
    label: 'Astuce',
    text: 'Utilisez les tableaux de bord pour suivre vos indicateurs en temps réel et repérer rapidement les écarts importants.',
  },
  {
    type: 'tip',
    label: 'Amélioration',
    text: 'Orchestrez vos flux, automatisez vos actions et connectez vos opérations dans une logique cohérente et scalable.',
  },
  {
    type: 'tip',
    label: 'Astuce',
    text: 'Structurez vos données pour faciliter la prise de décision et créer une base solide pour vos automatisations.',
  },
];

const NPS_STORAGE_KEY = 'nodeAuthNpsSubmittedAt';
const NPS_PENDING_STORAGE_KEY = 'nodeAuthNpsPending';
const NPS_DISPLAY_INTERVAL = 90 * 24 * 60 * 60 * 1000;
const GOOGLE_REVIEW_URL = 'https://g.page/r/CXkhp5TR7AaZEAE/review';
const WEATHER_CACHE_DURATION = 30 * 60 * 1000;
const weatherRequests = new Map();

const hasRecentNpsSubmission = (userId) => {
  if (!userId) return true;
  const submittedAt = Number(
    localStorage.getItem(`${NPS_STORAGE_KEY}:${userId}`)
  );
  return submittedAt > 0 && Date.now() - submittedAt < NPS_DISPLAY_INTERVAL;
};

const getAuthWeather = async (lat, lon, language) => {
  const requestKey = `${Number(lat).toFixed(2)}:${Number(lon).toFixed(2)}:${language}`;
  const storageKey = `nodeAuthWeather:${requestKey}`;

  try {
    const cached = JSON.parse(sessionStorage.getItem(storageKey));
    if (
      cached?.weather &&
      Date.now() - cached.cachedAt < WEATHER_CACHE_DURATION
    ) {
      return cached.weather;
    }
  } catch (error) {}

  if (weatherRequests.has(requestKey)) {
    return weatherRequests.get(requestKey);
  }

  const request = nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/weather',
    noAuth: true,
    body: { lat, lon, language },
  })
    .then((response) => {
      const weather = (response?.data || response)?.weather;
      if (weather) {
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({ weather, cachedAt: Date.now() })
        );
      }
      return weather;
    })
    .finally(() => weatherRequests.delete(requestKey));

  weatherRequests.set(requestKey, request);
  return request;
};

const WATERMARKS = [
  {
    src: '/assets/v3/img/login-radio-waves.svg',
    width: 300,
    top: '-90px',
    left: '-75px',
    duration: '18s',
    color: 'primary',
  },
  {
    src: '/assets/v3/img/login-arrow.svg',
    width: 230,
    top: '12%',
    right: '-22px',
    duration: '21s',
    color: 'secondary',
  },
  {
    src: '/assets/v3/img/login-cloud.svg',
    width: 270,
    bottom: '-20px',
    left: '14%',
    duration: '24s',
    color: 'primary',
  },
];

const getStoredBranding = () => {
  try {
    return JSON.parse(localStorage.getItem('businessBranding')) || {};
  } catch (error) {
    return {};
  }
};

const WEATHER_ICON_CODES = {
  sunny: [1000],
  storm: [1087, 1273, 1276, 1279, 1282],
  snow: [
    1066, 1069, 1114, 1117, 1204, 1207, 1210, 1213, 1216, 1219, 1222, 1225,
    1237, 1249, 1252, 1255, 1258, 1261, 1264,
  ],
  fog: [1030, 1135, 1147],
  cloudy: [1003, 1006, 1009],
};

const getWeatherIcon = (code, color) => {
  const sx = { fontSize: 26, color };
  if (WEATHER_ICON_CODES.sunny.includes(code)) return <WbSunnyOutlinedIcon sx={sx} />;
  if (WEATHER_ICON_CODES.storm.includes(code)) return <ThunderstormOutlinedIcon sx={sx} />;
  if (WEATHER_ICON_CODES.snow.includes(code)) return <AcUnitOutlinedIcon sx={sx} />;
  if (WEATHER_ICON_CODES.fog.includes(code)) return <BlurOnOutlinedIcon sx={sx} />;
  if (WEATHER_ICON_CODES.cloudy.includes(code)) return <CloudOutlinedIcon sx={sx} />;
  return <GrainOutlinedIcon sx={sx} />;
};

const AuthLayout = ({ loading = false }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const businessData = useSelector((state) => state.core.businessData);
  const userId = useSelector((state) => state.core.user?.uid);
  const [branding, setBranding] = useState(getStoredBranding);
  const [weatherData, setWeatherData] = useState(null);
  const [coords, setCoords] = useState(null);
  const [now, setNow] = useState(new Date());
  const [activeSlide, setActiveSlide] = useState(0);
  const [npsSubmitted, setNpsSubmitted] = useState(() =>
    hasRecentNpsSubmission(userId)
  );
  const [npsScore, setNpsScore] = useState(null);
  const [npsFeedback, setNpsFeedback] = useState('');
  const [npsError, setNpsError] = useState('');

  const mainColor = branding.mainColor || theme.palette.primary.main;
  const secColor = branding.secColor || theme.palette.secondary.main;
  const activeContent = SLIDES[activeSlide];
  const primaryText = isDarkMode ? '#ffffff' : '#151519';
  const secondaryText = isDarkMode
    ? 'rgba(255,255,255,0.56)'
    : 'rgba(0,0,0,0.52)';
  const npsSelectedText =
    chroma.contrast(mainColor, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#111827';

  const moduleIcons = {
    bos: <NodeBOS primaryColor={mainColor} secondaryColor={secColor} />,
    aura: <NodeAura primaryColor={mainColor} secondaryColor={secColor} />,
    pulse: <NodePulse primaryColor={mainColor} secondaryColor={secColor} />,
    maestro: <NodeMaestro primaryColor={mainColor} secondaryColor={secColor} />,
  };
  const productIconScales = {
    bos: 1,
    aura: 1.18,
    pulse: 1,
    maestro: 1.18,
  };

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setNpsSubmitted(hasRecentNpsSubmission(userId));
    setNpsScore(null);
    setNpsFeedback('');
    setNpsError('');
    localStorage.removeItem(NPS_STORAGE_KEY);
    localStorage.removeItem(NPS_PENDING_STORAGE_KEY);
  }, [userId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((current) => (current + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      () => setCoords(null)
    );
  }, []);

  useEffect(() => {
    const lat = coords?.lat ?? businessData?.geo?._latitude;
    const lon = coords?.lon ?? businessData?.geo?._longitude;
    if (lat === undefined || lon === undefined) return;

    let cancelled = false;
    (async () => {
      const weather = await getAuthWeather(
        lat,
        lon,
        i18n.language?.slice(0, 2) || 'fr'
      );
      if (!cancelled && weather) {
        setWeatherData(weather);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    coords?.lat,
    coords?.lon,
    businessData?.geo?._latitude,
    businessData?.geo?._longitude,
    i18n.language,
  ]);

  useEffect(() => {
    const handleBrandingChange = (event) => {
      if (event.detail) setBranding(event.detail);
    };
    window.addEventListener('node:auth-branding-change', handleBrandingChange);
    return () =>
      window.removeEventListener(
        'node:auth-branding-change',
        handleBrandingChange
      );
  }, []);

  const formattedTime = now.toLocaleTimeString('fr-CA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const formattedDate = now
    .toLocaleDateString('fr-CA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
    .replace(/^\w/, (character) => character.toUpperCase());

  const markNpsSubmitted = () => {
    if (!userId) return;
    localStorage.setItem(`${NPS_STORAGE_KEY}:${userId}`, String(Date.now()));
    setNpsSubmitted(true);
  };

  const handleNpsFeedbackSubmit = () => {
    if (!userId) return;
    if (npsFeedback.trim().length < 5) {
      setNpsError('Partagez quelques mots avant d’envoyer.');
      return;
    }

    setNpsError('');
    try {
      localStorage.setItem(
        `${NPS_PENDING_STORAGE_KEY}:${userId}`,
        JSON.stringify({
          score: npsScore,
          feedback: npsFeedback.trim(),
          createdAt: Date.now(),
        })
      );
      markNpsSubmitted();
    } catch (error) {
      setNpsError('Impossible de conserver votre commentaire.');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
        boxSizing: 'border-box',
        backgroundColor: isDarkMode ? '#08080a' : '#f2f2f5',
        backgroundImage: `
          radial-gradient(circle at 12% 18%, ${chroma(mainColor)
            .alpha(isDarkMode ? 0.2 : 0.14)
            .css()} 0%, transparent 38%),
          radial-gradient(circle at 88% 80%, ${chroma(secColor)
            .alpha(isDarkMode ? 0.16 : 0.11)
            .css()} 0%, transparent 42%)
        `,
        '@media (prefers-reduced-motion: reduce)': {
          '& *': {
            animation: 'none !important',
            transition: 'none !important',
          },
        },
      }}
    >
      {WATERMARKS.map((watermark, index) => (
        <Box
          key={watermark.src}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            width: {
              xs: watermark.width * 0.65,
              md: watermark.width,
            },
            height: {
              xs: watermark.width * 0.65,
              md: watermark.width,
            },
            top: watermark.top,
            right: watermark.right,
            bottom: watermark.bottom,
            left: watermark.left,
            opacity: isDarkMode ? 0.18 : 0.13,
            backgroundColor:
              watermark.color === 'secondary' ? secColor : mainColor,
            maskImage: `url(${watermark.src})`,
            WebkitMaskImage: `url(${watermark.src})`,
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
            maskSize: 'contain',
            WebkitMaskSize: 'contain',
            animation: `${gentleFloat} ${watermark.duration} ease-in-out ${
              index * 2
            }s infinite`,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ))}

      <Box
        sx={{
          position: 'absolute',
          width: 480,
          height: 480,
          borderRadius: '50%',
          top: '-18%',
          right: '8%',
          background: chroma(mainColor).alpha(0.08).css(),
          filter: 'blur(100px)',
          animation: `${gentleFloat} 14s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          bottom: '-16%',
          left: '6%',
          background: chroma(secColor).alpha(0.07).css(),
          filter: 'blur(100px)',
          animation: `${gentleFloat} 16s ease-in-out 2s infinite reverse`,
          pointerEvents: 'none',
        }}
      />

      <Box
        className={isDarkMode ? 'glass-dark' : 'glass'}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 980,
          height: 'auto',
          minHeight: isMobile ? 'auto' : 620,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          borderRadius: { xs: '22px', md: '30px' },
          overflow: 'hidden',
          backgroundColor: isDarkMode
            ? 'rgba(8,8,11,0.68)'
            : 'rgba(255,255,255,0.62)',
          backdropFilter: 'blur(32px) saturate(150%)',
          WebkitBackdropFilter: 'blur(32px) saturate(150%)',
          border: `1px solid ${
            isDarkMode
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(255,255,255,0.76)'
          }`,
          boxShadow: isDarkMode
            ? '0 28px 90px rgba(0,0,0,0.34)'
            : '0 28px 90px rgba(31,38,135,0.13)',
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : '50%',
            minHeight: isMobile ? 230 : 'auto',
            p: { xs: 3, md: 6 },
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            color: primaryText,
            background: `linear-gradient(145deg, ${chroma(mainColor)
              .alpha(isDarkMode ? 0.08 : 0.05)
              .css()}, transparent 55%)`,
          }}
        >
          <Box>
            {typeof weatherData?.tempC === 'number' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: 0.75,
                  color: secondaryText,
                }}
              >
                {getWeatherIcon(weatherData.conditionCode, mainColor)}
                <Box sx={{ fontSize: 20, fontWeight: 650 }}>
                  {Math.round(weatherData.tempC)}°C
                </Box>
              </Box>
            )}
            <Box
              sx={{
                fontSize: { xs: 32, md: 52 },
                fontWeight: 650,
                letterSpacing: '-0.055em',
                lineHeight: 1,
              }}
            >
              {formattedTime}
            </Box>
            <Box
              sx={{
                mt: 1,
                fontSize: 13,
                color: secondaryText,
                letterSpacing: '0.01em',
              }}
            >
              {formattedDate}
            </Box>
          </Box>

          {!npsSubmitted ? (
            <Box sx={{ my: 'auto', py: { xs: 3, md: 4 } }}>
              <Box
                sx={{
                  color: mainColor,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                Votre expérience
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  maxWidth: 390,
                  fontSize: { xs: 17, md: 20 },
                  lineHeight: 1.35,
                  fontWeight: 650,
                  letterSpacing: '-0.02em',
                }}
              >
                Sur une échelle de 0 à 10, comment évaluez-vous votre expérience
                avec Node?
              </Box>
              <Box
                sx={{
                  mt: 2.5,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(11, minmax(0, 1fr))',
                  gap: 0.75,
                  maxWidth: 390,
                }}
              >
                {Array.from({ length: 11 }, (_, score) => {
                  const selected = npsScore === score;
                  return (
                    <Box
                      key={score}
                      component="button"
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setNpsScore(score);
                        setNpsError('');
                      }}
                      sx={{
                        minWidth: 0,
                        height: 38,
                        p: 0,
                        borderRadius: 2,
                        fontFamily: 'inherit',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                        color: selected ? npsSelectedText : primaryText,
                        backgroundColor: selected ? mainColor : 'transparent',
                        border: '1px solid',
                        borderColor: selected
                          ? mainColor
                          : isDarkMode
                            ? 'rgba(255,255,255,0.28)'
                            : 'rgba(21,21,25,0.28)',
                        '&:hover': {
                          borderColor: mainColor,
                          backgroundColor: selected
                            ? mainColor
                            : chroma(mainColor)
                                .alpha(isDarkMode ? 0.16 : 0.08)
                                .css(),
                        },
                      }}
                    >
                      {score}
                    </Box>
                  );
                })}
              </Box>
              <Box
                sx={{
                  mt: 0.75,
                  maxWidth: 390,
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: secondaryText,
                  fontSize: 10,
                }}
              >
                <span>À améliorer</span>
                <span>Excellente</span>
              </Box>

              {npsScore >= 8 && (
                <Box sx={{ mt: 2.5, maxWidth: 390 }}>
                  <Box sx={{ fontSize: 13, lineHeight: 1.55 }}>
                    Merci! Accepteriez-vous de partager votre expérience sur
                    Google?
                  </Box>
                  <Button
                    component="a"
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    onClick={markNpsSubmitted}
                    sx={{ mt: 1.5, borderRadius: 2.5, textTransform: 'none' }}
                  >
                    Laisser un avis Google
                  </Button>
                </Box>
              )}

              {npsScore !== null && npsScore < 8 && (
                <Box sx={{ mt: 2.5, maxWidth: 390 }}>
                  <TextField
                    value={npsFeedback}
                    onChange={(event) => setNpsFeedback(event.target.value)}
                    label="Comment pourrions-nous améliorer votre expérience?"
                    multiline
                    minRows={2}
                    fullWidth
                    inputProps={{ maxLength: 2000 }}
                    size="small"
                  />
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleNpsFeedbackSubmit}
                    sx={{ mt: 1.25, borderRadius: 2.5, textTransform: 'none' }}
                  >
                    Envoyer mon commentaire
                  </Button>
                  {npsError && (
                    <Box sx={{ mt: 1, color: 'error.main', fontSize: 11 }}>
                      {npsError}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ) : (
          <Box
            key={activeSlide}
            sx={{
              mt: 'auto',
              mb: { xs: 3, md: 4 },
              pt: { xs: 5, md: 6 },
              animation: `${slideReveal} 600ms cubic-bezier(0.16, 1, 0.3, 1)`,
            }}
          >
            {activeContent.type === 'module' ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ width: 38, height: 38, flexShrink: 0 }}>
                  {moduleIcons[activeContent.key]}
                </Box>
                <Box>
                  <Box
                    sx={{
                      fontSize: 18,
                      fontWeight: 650,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {activeContent.name}
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      maxWidth: 360,
                      fontSize: 14,
                      lineHeight: 1.65,
                      color: secondaryText,
                    }}
                  >
                    {activeContent.text}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box >
                <Box
                  sx={{
                    color: mainColor,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                  }}
                >
                   <Text
                    value={activeContent.label}
                    fontSize={13}
                    fontWeight={700}
                  />
                  
                </Box>
                <Box
                  sx={{
                    mt: 0.5,
                    maxWidth: 350,
                    fontSize: { xs: 13, md: 14 },
                    fontWeight: 400,
                  }}
                >
                  <Text
                    value={activeContent.text}
                    fontSize={11}
                  />
                </Box>
              </Box>
            )}
          </Box>
          )}

          {!isMobile && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {Object.entries(moduleIcons).map(([key, icon]) => (
                  <Box
                    key={key}
                    sx={{
                      width: key === "pulse" ? 18: key === "maestro" ? 23 : 24,
                      height: key === "pulse" ? 18 : key === "maestro" ? 23 : 24,
                      opacity: 0.72,
                      marginTop: key==="aura" ? '6px' : 0,
                      display: 'grid',
                      placeItems: 'center',
                      '& > svg': {
                        width: key === "pulse" ? 17 : key === "maestro" ? 24 : 20,
                        height: key === "pulse" ? 17 : key === "maestro" ? 24 : 20,
                        transform: `scale(${productIconScales[key]})`,
                        transformOrigin: 'center',
                      },
                    }}
                  >
                    {icon}
                  </Box>
                ))}
              </Box>
              <Box
                component="a"
                href="https://usenode.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-block',
                  mt: 2,
                  color: secondaryText,
                  fontSize: 10.5,
                  textDecoration: 'none',
                  '&:hover': { color: mainColor },
                }}
              >
                usenode.com
              </Box>
            </Box>
          )}
        </Box>

        <Divider
          orientation={isMobile ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            borderColor: isDarkMode
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0,0,0,0.08)',
          }}
        />

        <Box
          sx={{
            width: isMobile ? '100%' : '50%',
            p: { xs: 3, md: 6 },
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            {loading ? (
              <Box sx={{ minHeight: 120, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Outlet />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;
